import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { evaluateExerciseEligibility } from "@jilian/core";
import { EXERCISE_LIBRARY } from "../data/exercises";
import { useSettings } from "../db/hooks";
import { DEFAULT_INJURY } from "../db/repo";
import { Card, RiskTag } from "../components/ui";

const CATEGORIES = ["全部", "胸", "背", "肩", "手臂", "腿部孤立", "核心", "锁定"] as const;
const EQUIPMENTS = ["全部器械", "杠铃", "哑铃", "固定器械", "绳索", "徒手/自重"] as const;

export function categoryOf(nameZh: string): string {
  if (nameZh.includes("胸")) return "胸";
  if (nameZh.includes("背")) return "背";
  if (nameZh.includes("推肩") || nameZh.includes("侧平举") || nameZh.includes("后束") || nameZh.includes("后束飞鸟")) return "肩";
  if (nameZh.includes("弯举") || nameZh.includes("下压") || nameZh.includes("臂屈伸") || nameZh.includes("过头") || nameZh.includes("碎颅")) return "手臂";
  if (nameZh.includes("腿屈伸") || nameZh.includes("腿弯举") || nameZh.includes("髋外展") || nameZh.includes("髋内收") || nameZh.includes("臀推")) return "腿部孤立";
  if (nameZh.includes("卷腹") || nameZh.includes("仰卧") || nameZh.includes("平板支撑") || nameZh.includes("健腹轮") || nameZh.includes("举腿")) return "核心";
  return "其他";
}

export function matchesEquipment(e: (typeof EXERCISE_LIBRARY)[0], eq: string): boolean {
  if (eq === "全部器械") return true;
  if (eq === "杠铃") return e.equipmentType === "barbell" || e.nameZh.includes("杠铃");
  if (eq === "哑铃") return e.equipmentType === "dumbbell" || e.nameZh.includes("哑铃");
  if (eq === "固定器械") return e.equipmentType === "machine" || e.nameZh.includes("器械") || e.nameZh.includes("机");
  if (eq === "绳索") return e.equipmentType === "cable" || e.nameZh.includes("绳索") || e.nameZh.includes("龙门架");
  if (eq === "徒手/自重") return e.equipmentType === "bodyweight" || e.equipmentType === "assisted_machine" || e.nameZh.includes("自重") || e.nameZh.includes("俯卧撑") || e.nameZh.includes("引体") || e.nameZh.includes("双杠");
  return true;
}

export function ExerciseLibrary() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState<string>("全部");
  const [eq, setEq] = useState<string>("全部器械");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const settings = useSettings();
  const injury = settings?.injury ?? DEFAULT_INJURY;

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return EXERCISE_LIBRARY.filter((e) => {
      const inCat = cat === "全部" ? true : cat === "锁定" ? e.lockedUntilMedicalClearance : categoryOf(e.nameZh) === cat;
      if (!inCat) return false;
      const inEq = matchesEquipment(e, eq);
      if (!inEq) return false;
      if (!needle) return true;
      return (
        e.nameZh.toLowerCase().includes(needle) ||
        e.nameEn.toLowerCase().includes(needle) ||
        e.primaryMuscles.some((m) => m.toLowerCase().includes(needle))
      );
    });
  }, [q, cat, eq]);

  return (
    <div className="min-h-screen mx-auto max-w-[480px] px-4 pt-safe-top pb-10">
      {/* 顶部返回导航栏 */}
      <div className="flex items-center justify-between py-3 mb-2">
        <button
          onClick={() => nav(-1)}
          className="inline-flex items-center gap-1 text-tone-accent font-semibold text-sm active:opacity-70"
        >
          <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          返回
        </button>
        <h1 className="text-[17px] font-bold text-tone-ink">动作库</h1>
        <div className="w-12" />
      </div>

      <div className="mb-3">
        <div className="text-xs font-semibold text-tone-soft">
          共 {EXERCISE_LIBRARY.length} 个动作 · 当前匹配 {filtered.length} 个
        </div>
      </div>

      {/* 搜索框 */}
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="搜索动作 / 杠铃 / 哑铃 / 器械 / 肌群"
        className="w-full min-h-[46px] px-4 rounded-2xl bg-black/5 dark:bg-white/10 backdrop-blur-md border border-white/60 dark:border-white/10 text-tone-ink mb-2.5 text-sm outline-none focus:ring-2 focus:ring-tone-accent/50"
      />

      {/* 一级筛选：部位分类 */}
      <div className="flex gap-1.5 flex-wrap mb-2">
        {CATEGORIES.map((c) => (
          <button
            key={c}
            onClick={() => setCat(c)}
            className={`min-h-[34px] px-3.5 rounded-full text-[12px] font-semibold transition-all active:scale-95 ${
              cat === c
                ? "bg-tone-accent text-white shadow-sm shadow-tone-accent/25"
                : "bg-black/5 dark:bg-white/10 text-tone-soft hover:text-tone-ink"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {/* 二级筛选：器械分类 */}
      <div className="flex gap-1.5 flex-wrap mb-4 p-1.5 rounded-2xl bg-black/5 dark:bg-white/5 backdrop-blur-md">
        {EQUIPMENTS.map((item) => (
          <button
            key={item}
            onClick={() => setEq(item)}
            className={`min-h-[30px] px-3 rounded-xl text-[11px] font-semibold transition-all active:scale-95 ${
              eq === item
                ? "bg-white dark:bg-white/20 text-tone-ink shadow-sm"
                : "text-tone-soft hover:text-tone-ink"
            }`}
          >
            {item}
          </button>
        ))}
      </div>

      {/* 动作列表 */}
      <div className="space-y-2.5">
        {filtered.map((e) => {
          const avail = evaluateExerciseEligibility({ exercise: e, injury });
          const isLocked = e.lockedUntilMedicalClearance;
          const isExpanded = expandedId === e.id;

          return (
            <Card
              key={e.id}
              className="p-3.5 transition-all cursor-pointer hover:border-tone-accent/40"
              onClick={() => setExpandedId(isExpanded ? null : e.id)}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[15px] font-bold text-tone-ink">{e.nameZh}</span>
                    {isLocked ? (
                      <RiskTag tone="danger">医疗锁锁定</RiskTag>
                    ) : !avail.isEnabledForCurrentInjury ? (
                      <RiskTag tone="warn">伤情受限</RiskTag>
                    ) : (
                      <RiskTag tone="good">可用</RiskTag>
                    )}
                  </div>
                  <div className="text-[11px] text-tone-soft font-mono mt-0.5">{e.nameEn}</div>
                </div>

                <div className="text-right shrink-0">
                  <span className="text-[11px] font-semibold text-tone-soft">
                    {e.compoundType === "compound" ? "复合" : "孤立"} · {e.posture === "seated" ? "坐姿" : e.posture === "lying" ? "卧姿" : e.posture === "chest_supported" ? "胸托" : "站姿"}
                  </span>
                </div>
              </div>

              {/* 肌群与规格标签 */}
              <div className="flex items-center gap-1.5 flex-wrap mt-2 text-[11px]">
                <span className="px-2 py-0.5 rounded-md bg-tone-accent/10 text-tone-accent font-medium">
                  {e.primaryMuscles.join(" / ")}
                </span>
                <span className="px-2 py-0.5 rounded-md bg-black/5 dark:bg-white/10 text-tone-soft">
                  稳定性: {e.stabilityLevel === "high" ? "高" : e.stabilityLevel === "medium" ? "中" : "低"}
                </span>
                <span className="px-2 py-0.5 rounded-md bg-black/5 dark:bg-white/10 text-tone-soft">
                  {e.defaultSets}组 × {e.repMin}–{e.repMax}次
                </span>
              </div>

              {/* 展开详情 */}
              {isExpanded ? (
                <div className="mt-3 pt-3 border-t border-tone-border text-[12px] space-y-2 text-tone-soft">
                  {e.safetyInstructions.length > 0 ? (
                    <div>
                      <div className="font-semibold text-tone-ink mb-0.5">安全要点：</div>
                      <ul className="list-disc pl-4 space-y-0.5">
                        {e.safetyInstructions.map((s, i) => (
                          <li key={i}>{s}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  {isLocked ? (
                    <div className="p-2 rounded-xl bg-tone-danger/10 text-tone-danger text-[11px] leading-relaxed">
                      ⚠️ 此动作为下肢/复合承重动作，受右脚小脚趾骨折硬约束锁定。需专业医疗许可与承重阶段解锁后方可启用。
                    </div>
                  ) : !avail.isEnabledForCurrentInjury ? (
                    <div className="p-2 rounded-xl bg-tone-warn/10 text-tone-warn text-[11px] leading-relaxed">
                      ⚠️ 依据当前伤情约束（{injury.bodyPart}，承重等级：{injury.weightBearingLevel}），此动作暂不满足执行条件。
                    </div>
                  ) : null}
                </div>
              ) : null}
            </Card>
          );
        })}

        {filtered.length === 0 ? (
          <div className="text-center py-12 text-tone-soft text-sm">
            未找到匹配动作，请调整搜索词或筛选条件
          </div>
        ) : null}
      </div>
    </div>
  );
}
